import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { FormSpy } from "react-final-form";

import { Button } from "@material-ui/core";

import TagsStore from "stores/TagsStore";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl from "components/FormControl";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import { SNACKBAR_TYPES } from "components/Snackbar";
import SimpleField from "components/SimpleField";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import {
  FormContainer,
  ControlsContainer,
  ControlContainer,
  Row,
} from "./styles";

type Props = RouteComponentProps & SnackbarContextProps & {};

@observer
class Tags extends React.Component<Props> {
  @observable isEditMode: boolean = false;
  @observable isPopupDirty: boolean = false;
  @observable isCloseConfirmationPopupOpened: boolean = false;
  @observable isSaveConfirmationPopupOpened: boolean = false;
  @observable options: any[] = [];
  @observable onAccept: () => void = () => {};

  @computed get existingTagsIds() {
    return TagsStore.items.map(({ id }) => id);
  }

  @computed get displayValues(): any {
    return {
      tags: TagsStore.items?.map(
        ({ id, title }: { id: number; title: string }) => ({ id, name: title }),
      ),
    };
  }

  @computed get initialValues() {
    return { tags: TagsStore.items };
  }

  async componentDidMount() {
    await TagsStore.fetch();
    this.initOptions();
  }

  componentWillUnmount() {
    TagsStore.reset();
  }

  initOptions = () =>
    (this.options = TagsStore.items.map(({ id, title }) => ({
      value: { id, title },
      label: title,
    })));

  getDeleteTagPopupText = (optionName: string) =>
    `Are you sure you want to clear the ${optionName} tag?`;

  closeConfirmationPopup = () => {
    this.isCloseConfirmationPopupOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.isCloseConfirmationPopupOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    const onAccept = () => {
      this.exitEditMode();
      this.closeConfirmationPopup();
      this.resetIsPopupDirty();
      resetForm();
    };
    this.onCloseForm(onAccept);
  };

  onEditClick = () => {
    this.isEditMode = true;
  };

  exitEditMode = () => {
    this.isEditMode = false;
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  onCreateOption = (option: string) => {
    const newOption = {
      value: { id: option, title: option, isNewOption: true },
      label: option,
    };

    this.options.push(newOption);
    return newOption;
  };

  openSaveConfirmation = () => {
    this.isSaveConfirmationPopupOpened = true;
  };

  closeSaveConfirmation = () => {
    this.isSaveConfirmationPopupOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeSaveConfirmation();
    this.submit(values);
  };

  onSubmit = () => {
    this.openSaveConfirmation();
  };

  submit = async (values: any) => {
    const { setMessage } = this.props;
    const { tags } = values;

    const updatedTagsIds = tags
      .filter(({ isNewOption }: { isNewOption?: boolean }) => !isNewOption)
      .map(({ id }: { id: number }) => id);

    const payloadAddTags = {
      tags: tags
        ?.filter(({ isNewOption }: { isNewOption?: boolean }) => isNewOption)
        .map(({ title }: { title: string }) => title),
    };

    const payloadDeleteTags = {
      ids: this.existingTagsIds.filter(
        (id: number) => !updatedTagsIds.includes(id),
      ),
    };

    try {
      payloadAddTags.tags?.length && (await TagsStore.addTags(payloadAddTags));
      payloadDeleteTags.ids?.length &&
        (await TagsStore.deleteTags(payloadDeleteTags));

      this.resetIsPopupDirty();
      this.exitEditMode();
      await TagsStore.fetch();
      this.initOptions();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page title="Tags" isLoading={TagsStore.isLoading}>
          {this.isCloseConfirmationPopupOpened && (
            <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
              <ConfirmationDialog
                title="Discard Changes"
                text="Are you sure you would like to discard changes? All information will be lost."
                declineText="Cancel"
                acceptText="Discard"
                onDecline={this.closeConfirmationPopup}
                onAccept={this.onAccept}
              />
            </Popup>
          )}
          <FormControl
            onSubmit={this.onSubmit}
            initialValues={this.initialValues}
          >
            {this.isSaveConfirmationPopupOpened && (
              <FormSpy subscription={{ values: true }}>
                {({ values }) => (
                  <Popup onClose={this.closeSaveConfirmation} withoutExitButton>
                    <ConfirmationDialog
                      title="Save Tags"
                      text="Are you sure you want to save tags?"
                      acceptText="Save"
                      declineText="Cancel"
                      onAccept={this.confirmUpdate(values)}
                      onDecline={this.closeSaveConfirmation}
                    />
                  </Popup>
                )}
              </FormSpy>
            )}
            <FormSpy
              subscription={{ dirty: true }}
              onChange={this.onChangeIsPopupDirty}
            />
            <FormContainer>
              <ControlsContainer>
                {this.isEditMode ? (
                  <>
                    <ControlContainer>
                      <CancelButtonControl
                        render={this.renderCancelButton}
                        onClick={this.onCancelClick}
                      />
                    </ControlContainer>
                    <ControlContainer>
                      <SubmitButtonControl render={this.renderButton} />
                    </ControlContainer>
                  </>
                ) : (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )}
              </ControlsContainer>

              <Row>
                {this.isEditMode ? (
                  <DropdownFieldControl
                    name="tags"
                    label="Tags"
                    onCreateOption={this.onCreateOption}
                    isLoading={TagsStore.isLoading}
                    options={this.options}
                    deletePopupProps={{
                      title: "Clear Tag",
                      getText: this.getDeleteTagPopupText,
                      acceptText: "Clear",
                      declineText: "Cancel",
                    }}
                    multi
                    isCreatable
                  />
                ) : (
                  <SimpleField label="Tags" value={this.displayValues?.tags} />
                )}
              </Row>
            </FormContainer>
          </FormControl>
        </Page>
      </Container>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={TagsStore.isManaging}
      fullWidth
    >
      Save
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(Tags));
